/**
 * ImportDslPanel — admin workbench for the Import DSL (see IMPORT_DSL_SPEC.md).
 * Replaces the multi-step import wizard with editable DSL: the script declares a
 * destination (connection/database/table), source options, and either a column
 * mapping or a per-row `each row { }` block. Analyze compiles it (server-side,
 * no file) into an import config; Save persists the DSL `script` (the server
 * derives `config_json` from it) as a reusable import definition. Running it (ImportRunner) and
 * editing it (re-open here) are unchanged from the wizard path.
 *
 * Authoring is file-free: header names are typed (`from = "Code"`) and validated
 * against the real file at run time.
 */
import { createSignal, createResource, createMemo, onMount, Show, Switch, Match, For } from 'solid-js';
import { api } from '../lib/api.js';
import { analyzeForm } from '../lib/formsdsl-api.js';
import { createImportDef, updateImportDef, getImportDef } from '../lib/importdefs.js';
import CodeMirrorDSL from '../components/CodeMirrorDSL.jsx';
import FloatingPanel from '../components/FloatingPanel.jsx';
import { toast } from '../lib/toast.js';
import { idslEditorSplit, setIdslEditorSplit } from '../lib/prefs.js';
import { makeSplitterDrag } from '../lib/splitter.js';

const EXAMPLE = `import {
  connection = "Metalindo"          # connection NAME (resolved on save)
  database   = "dbfastretail"
  table      = "m_customer"

  source = file { format = "csv"; header = true; delimiter = auto; encoding = "utf8" }
  strategy = upsert                 # insert | upsert
  onError  = ask                    # skip | ask

  column KodeCust  { from = "Code" }                            # file header → column
  column NamaCust  { from = "Name" }
  column TglDaftar { from = "RegDate"; format = "dd/MM/yyyy" }  # date format
  column Cabang    { prefill = "SELECT @vKodeCust" }            # value from SQL
  column Status    { from = "Stat"; enum = "SELECT kode FROM m_status" }  # allowed values
  # unmentioned target columns are skipped
}`;

const EACH_ROW_EXAMPLE = `import {
  connection = "Metalindo"; database = "dbfastretail"; table = "tr_jual"
  source = file { format = "xlsx"; header = true; sheet = "Sheet1" }
  onError = ask
  transaction = row              # each row atomic (statement = autocommit, default)
  each row {
    # each file column binds as @v<TitleCase(header)>; :Foo → @vFoo.
    # conditions read the same columns by name (Qty, Kode, …).
    require "SELECT 1 FROM ms_brg WHERE KodeBrg = :Kode" else "Barang tidak dikenal"
    when Qty > 0 {
      call "sp_import_jual(:NoNota, :Kode, :Qty)"
    }
    if Status == "BATAL" {
      exec "UPDATE tr_jual SET batal = 1 WHERE no = :NoNota"
    } else {
      call "sp_recalc(:NoNota)"
    }
  }
}`;

export default function ImportDslPanel(props) {
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });

  const [script, setScript] = createSignal(EXAMPLE);
  const [plan, setPlan] = createSignal(null);
  const [busy, setBusy] = createSignal(false);
  const [saving, setSaving] = createSignal(false);
  const [name, setName] = createSignal('');
  const [description, setDescription] = createSignal('');
  const [defId, setDefId] = createSignal(props.editId || null);
  const [helpOpen, setHelpOpen] = createSignal(false);
  let cmView;

  const imp = () => (plan()?.kind === 'import' ? plan().import : null);
  const diags = () => plan()?.diagnostics || [];

  // Editor/preview splitter — drag to resize; ratio (editor's fraction of the
  // width) is clamped + persisted (shared with the Forms DSL workbench).
  let splitHostEl;
  const startSplitterDrag = makeSplitterDrag(() => splitHostEl, setIdslEditorSplit);

  // Inline editor diagnostics (same analyzer as Analyze).
  const lint = async (code) => {
    try { return (await analyzeForm(code)).plan?.diagnostics || []; } catch { return []; }
  };

  const analyze = async () => {
    setBusy(true);
    try {
      const r = await analyzeForm(script());
      setPlan(r.plan);
      const n = (r.plan?.diagnostics || []).length;
      if (n) toast.error(`${n} diagnostic${n === 1 ? '' : 's'} — see preview`);
      else if (r.plan?.kind !== 'import') toast.error('This is not an import { } script.');
      else toast.success('Analyzed');
    } catch (e) {
      toast.error('Analyze failed: ' + (e?.message || e));
    } finally { setBusy(false); }
  };

  // Resolve the DSL's connection NAME → a connection id (case-insensitive).
  const resolveConn = (connName) => {
    const want = String(connName || '').trim().toLowerCase();
    return (conns() || []).find((c) => (c.name || '').toLowerCase() === want);
  };

  const save = async () => {
    if (!name().trim()) { toast.error('Give the import a name.'); return; }
    setSaving(true);
    try {
      // Re-analyze the exact text being saved: the destination (connection /
      // database / table) must come from THIS script, not from an earlier
      // Analyze of text that has since been edited.
      const src = script();
      const r = await analyzeForm(src);
      setPlan(r.plan);
      const im = imp();
      if (!im) { toast.error('This is not a valid import { } script — see the preview.'); return; }
      if (diags().length) { toast.error('Fix the diagnostics before saving.'); return; }
      const conn = resolveConn(im.connection);
      if (!conn) {
        toast.error(`Connection “${im.connection}” not found. Available: ${(conns() || []).map((c) => c.name).join(', ') || '(none)'}`);
        return;
      }
      const body = {
        name: name().trim(), description: description().trim(),
        connectionId: conn.id, databaseName: im.database, tableName: im.table,
        // The server derives config_json from the script (formsdsl ImportPlan.Config).
        script: src,
      };
      if (defId()) {
        await updateImportDef(defId(), body);
        toast.success('Import updated.');
      } else {
        const r = await createImportDef(body);
        if (r?.id) setDefId(r.id);
        toast.success(`Saved reusable import “${name().trim()}”.`);
      }
      props.onSaved?.();
    } catch (e) {
      toast.error('Save failed: ' + (e?.message || e));
    } finally { setSaving(false); }
  };

  // Open an existing DSL-built definition into the editor (Edit-as-DSL).
  const open = async (id) => {
    try {
      const def = await getImportDef(id);
      setDefId(def.id); setName(def.name || ''); setDescription(def.description || '');
      setScript(def.script || EXAMPLE);
      setPlan(null);
    } catch (e) { toast.error('Open failed: ' + (e?.message || e)); }
  };

  onMount(() => { if (props.editId) open(props.editId); });

  const insert = (code) => {
    const v = cmView;
    if (!v) { setScript(code); return; }
    const pos = v.state.selection.main.head;
    v.dispatch({ changes: { from: pos, insert: code }, selection: { anchor: pos + code.length } });
    v.focus();
  };

  return (
    <div class="fdsl-panel">
      <div class="fdsl-toolbar">
        <Show when={props.onBack}>
          <button class="btn btn-sm btn-ghost" onClick={() => props.onBack()}>← Back</button>
        </Show>
        <button class="btn btn-primary btn-sm" disabled={busy()} onClick={analyze}>{busy() ? 'Analyzing…' : '▶ Analyze'}</button>
        <button class="btn btn-sm btn-ghost" onClick={() => insert('\n\n' + EACH_ROW_EXAMPLE)} title="Insert a per-row (each row) import template">＋ each row</button>
        <button class="btn btn-sm btn-ghost" onClick={() => setHelpOpen((v) => !v)} title="Import DSL quick reference">📖 Help</button>
        <span class="spacer" />
        <span class="muted" style="font-size:11.5px;">Import DSL workbench (admin)</span>
      </div>

      <div class="fdsl-toolbar fdsl-toolbar-2">
        <input class="input fdsl-name" placeholder="Import name" value={name()} onInput={(e) => setName(e.currentTarget.value)} />
        <input class="input" style="flex:1;min-width:120px;" placeholder="Description (optional)" value={description()} onInput={(e) => setDescription(e.currentTarget.value)} />
        <button class="btn btn-sm btn-primary" disabled={saving()} onClick={save}>{saving() ? 'Saving…' : (defId() ? '💾 Save' : '💾 Save new')}</button>
      </div>

      <ImportDslHelp open={helpOpen()} onClose={() => setHelpOpen(false)} />

      <div class="fdsl-split" ref={splitHostEl}>
        <div class="fdsl-editor" style={`flex-basis: ${(idslEditorSplit() * 100).toFixed(2)}%;`}>
          <CodeMirrorDSL value={script()} onInput={setScript} lint={lint} onViewReady={(v) => (cmView = v)} />
        </div>
        <div class="fdsl-splitter" onPointerDown={startSplitterDrag} title="Drag to resize" />
        <div class="fdsl-preview">
          <Show when={plan()} fallback={<div class="fdsl-empty">Write an <code>import {'{ }'}</code> script and click Analyze.</div>}>
            <ImportPlanPreview plan={plan()} imp={imp()} resolveConn={resolveConn} />
          </Show>
        </div>
      </div>
    </div>
  );
}

// ── Compiled-plan preview (read-only) ───────────────────────────────────────────
function ImportPlanPreview(props) {
  const diags = () => props.plan?.diagnostics || [];
  const im = () => props.imp;
  const connFound = createMemo(() => (im() ? !!props.resolveConn(im().connection) : false));

  return (
    <div class="import-dsl-preview">
      <Show when={diags().length}>
        <div class="import-warn">
          <strong>{diags().length} diagnostic{diags().length === 1 ? '' : 's'}:</strong>
          <ul style="margin:6px 0 0 0;padding-left:18px;">
            <For each={diags()}>{(d) => <li><span class="mono">{d.code}</span> — {d.msg} <span class="muted">(line {d.line})</span></li>}</For>
          </ul>
        </div>
      </Show>

      <Show when={props.plan?.kind !== 'import' && !diags().length}>
        <div class="import-warn">This script is not an <span class="mono">import {'{ }'}</span> block.</div>
      </Show>

      <Show when={im()}>
        <div class="import-section-title">Destination</div>
        <div class="import-dsl-dest mono">
          {im().connection || '—'} · {im().database || '—'} / {im().table || '—'}
          <Show when={im().connection && !connFound()}>
            <div class="import-warn import-warn-soft" style="margin-top:6px;">Connection “{im().connection}” is not in your connection list — Save will be blocked.</div>
          </Show>
        </div>

        <div class="import-section-title">Source</div>
        <div class="muted mono" style="font-size:12.5px;">
          {(im().options?.format || 'auto')}{im().options?.hasHeader ? ', header' : ', no header'}
          {im().options?.delimiter ? `, delim ${JSON.stringify(im().options.delimiter)}` : ', auto-delim'}
          {im().options?.encoding ? `, ${im().options.encoding}` : ''}
          {im().options?.sheet ? `, sheet ${im().options.sheet}` : ''}
        </div>

        <div class="import-section-title">
          Mode: <span class="mono">{im().destination}</span>
          {(im().destination === 'execute' || im().destination === 'generate') ? ` · ${im().strategy} · onError ${im().onError}` : ` · onError ${im().onError}`}
          <Show when={im().destination === 'eachrow'}> · <span class="mono">transaction = {im().transaction}</span></Show>
        </div>

        <Switch fallback={
          <div class="import-preview-wrap">
            <table class="import-preview">
              <thead><tr><th>Target</th><th>Mode</th><th>Source / SQL</th></tr></thead>
              <tbody>
                <For each={im().columns || []}>{(c) => (
                  <tr>
                    <td class="mono">{c.target}</td>
                    <td>{c.mode}</td>
                    <td class="mono">{c.mode === 'prefill' || c.mode === 'enum' ? c.prefillQuery : c.source}</td>
                  </tr>
                )}</For>
              </tbody>
            </table>
          </div>
        }>
          <Match when={im().destination === 'sql'}>
            <div class="muted" style="font-size:12px;">Runs this once per row (file columns bound as <span class="mono">@v…</span>):</div>
            <textarea class="input import-sql-out" readonly rows="6">{im().rowSql || ''}</textarea>
          </Match>
          <Match when={im().destination === 'eachrow'}>
            <div class="muted" style="font-size:12px;">Per-row engine — runs these steps once per row (columns bound as <span class="mono">@v…</span>):</div>
            <div class="import-eachrow-steps mono">
              <For each={im().eachRow || []}>{(s) => <ImportStepLine step={s} depth={0} />}</For>
            </div>
          </Match>
        </Switch>

        <Show when={Object.keys(im().dateFormats || {}).length}>
          <div class="import-section-title">Date formats</div>
          <div class="muted mono" style="font-size:12px;">
            <For each={Object.entries(im().dateFormats)}>{([h, f]) => <div>{h} → {f}</div>}</For>
          </div>
        </Show>
      </Show>
    </div>
  );
}

// One per-row step, rendered as a readable line (control flow indents its body).
function ImportStepLine(props) {
  const s = () => props.step;
  const pad = () => ({ 'padding-left': `${props.depth * 14}px` });
  return (
    <Switch fallback={<div style={pad()}>{s().op} <span class="muted">{s().sql}</span></div>}>
      <Match when={s().op === 'require'}>
        <div style={pad()}>require <span class="muted">{s().sql}</span>{s().message ? <span class="muted"> else “{s().message}”</span> : null}</div>
      </Match>
      <Match when={s().op === 'if'}>
        <div style={pad()}>if …</div>
        <For each={s().then || []}>{(c) => <ImportStepLine step={c} depth={props.depth + 1} />}</For>
        <Show when={(s().else || []).length}>
          <div style={pad()}>else</div>
          <For each={s().else || []}>{(c) => <ImportStepLine step={c} depth={props.depth + 1} />}</For>
        </Show>
      </Match>
      <Match when={s().op === 'when'}>
        <div style={pad()}>when …</div>
        <For each={s().then || []}>{(c) => <ImportStepLine step={c} depth={props.depth + 1} />}</For>
      </Match>
      <Match when={s().op === 'for'}>
        <div style={pad()}>for {s().var} in sql.rows(…)</div>
        <For each={s().then || []}>{(c) => <ImportStepLine step={c} depth={props.depth + 1} />}</For>
      </Match>
    </Switch>
  );
}

// ── Quick reference ─────────────────────────────────────────────────────────────
function ImportDslHelp(props) {
  return (
    <FloatingPanel open={props.open} onClose={props.onClose}
                   title="📖 Import DSL — quick reference" width={680} maxHeight="78vh">
      <div class="fp-scroll import-dsl-help">
        <table class="import-dsl-help-tbl">
        <tbody>
          <tr><td class="mono">connection / database / table</td><td>Destination. <code>connection</code> is the connection NAME (resolved to an id on save).</td></tr>
          <tr><td class="mono">source = file {'{ … }'}</td><td><span class="mono">format</span> (csv/xlsx), <span class="mono">header</span> (true/false), <span class="mono">delimiter</span> (<span class="mono">auto</span> or <span class="mono">","</span>), <span class="mono">encoding</span>, <span class="mono">sheet</span>.</td></tr>
          <tr><td class="mono">strategy</td><td><span class="mono">insert</span> or <span class="mono">upsert</span> (ON DUPLICATE KEY UPDATE).</td></tr>
          <tr><td class="mono">onError</td><td><span class="mono">skip</span> (skip + report) or <span class="mono">ask</span> (pause on each error).</td></tr>
          <tr><td class="mono">column X {'{ from = "H" }'}</td><td>Map target <span class="mono">X</span> from file header <span class="mono">H</span> (defaults to the target name).</td></tr>
          <tr><td class="mono">column X {'{ prefill = "SQL" }'}</td><td>Fill <span class="mono">X</span> from a scalar SELECT; input cells bind as <span class="mono">@v&lt;Target&gt;</span>.</td></tr>
          <tr><td class="mono">column X {'{ from="H"; enum="SQL" }'}</td><td>Constrain header <span class="mono">H</span> to the values the SQL returns (case-insensitive, normalised).</td></tr>
          <tr><td class="mono">column X {'{ format = "dd/MM/yyyy" }'}</td><td>Parse the source header as a date with this mask.</td></tr>
          <tr><td class="mono">each row {'{ … }'}</td><td>Per-row engine instead of a column map. Each file column binds as <span class="mono">@v&lt;TitleCase(header)&gt;</span>; <span class="mono">:Foo</span> → <span class="mono">@vFoo</span> in SQL, and conditions read columns by name (<span class="mono">Qty</span>, <span class="mono">Status</span>).</td></tr>
          <tr><td class="mono">call / exec "SQL"</td><td>Run a procedure / statement for the row (sequential).</td></tr>
          <tr><td class="mono">require "SELECT …" else "msg"</td><td>Reject the row (skip/pause per <span class="mono">onError</span>) when the guard returns no row.</td></tr>
          <tr><td class="mono">if … {'{ }'} else {'{ }'} · when … {'{ }'}</td><td>Conditional steps — e.g. <span class="mono">when Qty &gt; 0 {'{ … }'}</span>, <span class="mono">if Status == "BATAL" {'{ … }'}</span>. Supports <span class="mono">and/or/not</span>, comparisons, <span class="mono">is set/empty</span>, <span class="mono">in […]</span>.</td></tr>
          <tr><td class="mono">for v in sql.rows("…") {'{ }'}</td><td>Loop a nested query run with the row's binds (header → detail). Each result binds as <span class="mono">@v&lt;v&gt;_&lt;col&gt;</span>, so the body uses <span class="mono">:v.col</span>; conditions can read <span class="mono">v.col</span>.</td></tr>
          <tr><td class="mono">transaction = row | statement</td><td>Per-row failure mode: <span class="mono">row</span> = each row in its own transaction (atomic); <span class="mono">statement</span> = autocommit each statement (default).</td></tr>
        </tbody>
        </table>
      </div>
    </FloatingPanel>
  );
}
